import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { AxiosResponse } from 'axios';
import { toast } from 'react-toastify';

import ApiService from '../../services/apiService';
import { Filters } from '../../interfaces/filters';
import { Area } from '../../interfaces/services/areas';
import { Order, OrderResponse } from '../../interfaces/services/orders';
import { ApiStatus, EquipmentTypeCode } from '../../utils/enums';
import { getTTCBaseUrl } from '../../utils/environmentUtils';

interface AreaState {
    detailsApiStatus: ApiStatus;
    details: Area;
    inboundOrdersApiStatus: ApiStatus;
    inboundOrders: Order[];
    outboundOrdersApiStatus: ApiStatus;
    outboundOrders: Order[];
}

/** Thunks */
export const fetchAreaDetails = createAsyncThunk(
    'area/fetchDetails',
    async ({
        service_area_id,
        filter_criteria,
    }: {
        service_area_id: string;
        filter_criteria?: Filters;
    }) => {
        try {
            const { data } = await ApiService.post({
                url: `${getTTCBaseUrl()}/planning/v2/insights/network/area-details`,
                body: {
                    service_area_id,
                    filter_criteria,
                },
            }) as AxiosResponse<Area>;

            return data;
        } catch (error) {
            console.error('Unable to load area details', error);
            toast.error('Unable to load area details');
            throw error;
        }
    }
);

export const fetchInboundOrders = createAsyncThunk(
    'area/fetchInboundOrders',
    async ({
        delivery_service_area_id,
        mri_freight_order_specified_eqpt_code,
        filter_criteria,
    }: {
        delivery_service_area_id: string;
        mri_freight_order_specified_eqpt_code: EquipmentTypeCode;
        filter_criteria: Filters;
    }) => {
        try {
            const { data } = await ApiService.post({
                url: `${getTTCBaseUrl()}/planning/v2/insights/network/orders`,
                body: {
                    pickup_service_area_id: null,
                    delivery_service_area_id,
                    filter_criteria: {
                        ...filter_criteria,
                        mri_freight_order_specified_eqpt_code,
                    },
                },
            }) as AxiosResponse<OrderResponse>;

            return data;
        } catch (error) {
            console.error('Unable to load orders inbound', error);
            toast.error(`Unable to load orders inbound to ${delivery_service_area_id}`);
            throw error;
        }
    }
);

export const fetchOutboundOrders = createAsyncThunk(
    'area/fetchOutboundOrders',
    async ({
        pickup_service_area_id,
        mri_freight_order_specified_eqpt_code,
        filter_criteria,
    }: {
        pickup_service_area_id?: string;
        mri_freight_order_specified_eqpt_code: EquipmentTypeCode;
        filter_criteria: Filters;
    }) => {
        try {
            const { data } = await ApiService.post({
                url: `${getTTCBaseUrl()}/planning/v2/insights/network/orders`,
                body: {
                    pickup_service_area_id,
                    delivery_service_area_id: null,
                    filter_criteria: {
                        ...filter_criteria,
                        mri_freight_order_specified_eqpt_code,
                    },
                },
            }) as AxiosResponse<OrderResponse>;

            return data;
        } catch (error) {
            console.error('Unable to load orders outbound', error);
            toast.error(`Unable to load orders outbound from ${pickup_service_area_id}`);
            throw error;
        }
    }
);

const initialState: AreaState = {
    detailsApiStatus: ApiStatus.Idle,
    details: {
        service_area_id: '',
        service_area_name: '',
        service_area_latitude_value: 0,
        service_area_longitude_value: 0,
        average_daily_balance: null,
        service_area_yield_usd: null,
        service_area_metrics: null,
        inbound_metrics: null,
        outbound_metrics: null,
    },
    inboundOrdersApiStatus: ApiStatus.Idle,
    inboundOrders: [],
    outboundOrdersApiStatus: ApiStatus.Idle,
    outboundOrders: [],
};

export const areaSlice = createSlice({
    name: 'areas',
    initialState,
    reducers: {
        clearAreaDetailsData: (): AreaState => {
            return initialState;
        },
    },
    extraReducers(builder) {
        builder.addCase(fetchAreaDetails.pending, (state) => {
            state.detailsApiStatus = ApiStatus.Loading;
        });
        builder.addCase(fetchAreaDetails.fulfilled, (state, action) => {
            state.detailsApiStatus = ApiStatus.Success;
            state.details = action.payload;
        });
        builder.addCase(fetchAreaDetails.rejected, (state) => {
            state.detailsApiStatus = ApiStatus.Failure;
            state.details = initialState.details;
        });
        builder.addCase(fetchInboundOrders.pending, (state) => {
            state.inboundOrdersApiStatus = ApiStatus.Loading;
        });
        builder.addCase(fetchInboundOrders.fulfilled, (state, action) => {
            state.inboundOrdersApiStatus = ApiStatus.Success;
            state.inboundOrders = action.payload.freight_orders;
        });
        builder.addCase(fetchInboundOrders.rejected, (state) => {
            state.inboundOrdersApiStatus = ApiStatus.Failure;
            state.inboundOrders = initialState.inboundOrders;
        });
        builder.addCase(fetchOutboundOrders.pending, (state) => {
            state.outboundOrdersApiStatus = ApiStatus.Loading;
        });
        builder.addCase(fetchOutboundOrders.fulfilled, (state, action) => {
            state.outboundOrdersApiStatus = ApiStatus.Success;
            state.outboundOrders = action.payload.freight_orders;
        });
        builder.addCase(fetchOutboundOrders.rejected, (state) => {
            state.outboundOrdersApiStatus = ApiStatus.Failure;
            state.outboundOrders = initialState.outboundOrders;
        });
    },
});

export default areaSlice.reducer;

export const {
    clearAreaDetailsData,
} = areaSlice.actions;
